import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';
import { industriesData } from '@/data/index';
import { IndustryCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { Thermometer, CheckCircle2 } from 'lucide-react';

export default function Industries() {
  const caseHighlights = [
    {
      id: 'chemical',
      industry: '化工石化',
      material: 'FKM氟橡胶',
      code: 'FKM',
      application: '耐强酸碱管道密封',
      tempRange: '-30°C ~ 220°C',
      specs: [
        { label: '工作温度', value: '-30°C ~ 220°C' },
        { label: '耐腐蚀性', value: '强酸强碱' },
        { label: '使用寿命', value: '10年+' }
      ]
    },
    {
      id: 'rail',
      industry: '轨道交通',
      material: 'EPDM橡胶',
      code: 'EPDM',
      application: '车厢密封系统',
      tempRange: '-40°C ~ 150°C',
      specs: [
        { label: '防火标准', value: 'EN45545' },
        { label: '耐候性能', value: '优异' },
        { label: '使用环境', value: '户外全天候' }
      ]
    },
    {
      id: 'mining',
      industry: '矿山采掘',
      material: 'NR天然橡胶',
      code: 'NR',
      application: '输送带衬板',
      tempRange: '-50°C ~ 80°C',
      specs: [
        { label: '耐磨测试', value: 'DIN53516通过' },
        { label: '拉伸强度', value: '≥18.0 MPa' },
        { label: '断裂伸长率', value: '≥500%' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.FACTORY_8}
            alt="Industries Served"
            className="w-full h-full object-cover opacity-30 img-industrial"
          />
          <div className="hero-overlay" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 text-center px-4"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-0.5 bg-primary" />
            <span className="font-mono text-xs font-bold tracking-widest text-primary uppercase">
              INDUSTRIES SERVED
            </span>
            <div className="w-12 h-0.5 bg-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            应用行业
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            服务化工石化、建筑幕墙、汽车轨道交通、矿山采掘等多个工业领域
          </p>
        </motion.div>
      </section>

      <section className="py-24 px-4">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {industriesData.map((industry, index) => (
              <motion.div
                key={industry.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <IndustryCard industry={industry} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-4 bg-section-alt">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-0.5 bg-primary" />
              <span className="font-mono text-xs font-bold tracking-widest text-primary uppercase">
                CASE HIGHLIGHTS
              </span>
              <div className="w-12 h-0.5 bg-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">典型应用案例</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              真实工业场景验证，严苛标准测试通过
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {caseHighlights.map((caseItem, index) => (
              <motion.div
                key={caseItem.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="card-industrial p-6 relative overflow-hidden"
              >
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                
                <div className="mb-4">
                  <span className="badge-code">{caseItem.code}</span>
                </div>

                <h3 className="text-xl font-bold mb-2">
                  {caseItem.industry}
                </h3>
                <p className="text-lg text-foreground mb-1">
                  {caseItem.material}
                </p>
                <p className="text-muted-foreground mb-6">
                  {caseItem.application}
                </p>

                <div className="space-y-3">
                  {caseItem.specs.map((spec, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {spec.label}
                          </span>
                          <span className="param-value text-sm">
                            {spec.value}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-border flex items-center gap-2 text-sm text-muted-foreground">
                  <Thermometer className="w-4 h-4 text-primary" />
                  <span className="param-value">{caseItem.tempRange}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="card-industrial p-8 md:p-12 text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              找不到您的行业应用场景？
            </h3>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              我们的技术团队拥有20+年工业橡胶应用经验，可为您提供专业的材料选型建议和定制化解决方案
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={ROUTE_PATHS.CONTACT}
                className="btn-primary-industrial px-8 py-3 rounded-md inline-flex items-center justify-center"
              >
                联系技术团队
              </Link>
              <Link
                to={ROUTE_PATHS.SELECTION}
                className="btn-outline-industrial px-8 py-3 rounded-md inline-flex items-center justify-center"
              >
                查看选型矩阵
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}